// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
} from '~/components/common';
import { TruckFormModal } from '~/components/module/truck';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_TRUCK,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IQueryParamTruck, ITruck } from '~/utils/interface/truck';
import { truckActions } from '~/thunks/truck/truckSlice';
import { deleteTruck, getListTruck } from '~/thunks/truck/truckThunk';
import { IBaseMoreAction, IHeaderCellType, ISortBy } from '~/utils/interface/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { TRUCK_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './truck.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsTruck = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onEdit: (id: string) => void,
  onArchive: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<ITruck>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITruck) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record.name}</Box>
        </Stack>
      ),
    },
    {
      id: 'truckType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_truck_type')}</Box>
          <SortComponent name='truck type' sortType={getSortType('truck type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITruck) => <Box className={cx('textValue')}>{record?.truckType || EMPTY_VALUE}</Box>,
    },
    {
      id: 'numberOfSeats',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_number_of_seats')}</Box>
          <SortComponent name='number of seats' sortType={getSortType('number of seats')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITruck) => <Box className={cx('textValue')}>{record?.numberOfSeats || EMPTY_VALUE}</Box>,
    },
    {
      id: 'rentalStatus',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_rental_status')}</Box>
          <SortComponent name='rental status' sortType={getSortType('rental status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITruck) => <Box className={cx('textValue')}>{record?.rentalStatus || EMPTY_VALUE}</Box>,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITruck) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'linxupPersonName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_device_lixnup')}</Box>
          <SortComponent name='rental status' sortType={getSortType('rental status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITruck) => <Box className={cx('textValue')}>{record?.linxupPersonName || EMPTY_VALUE}</Box>,
    },
    {
      id: 'action',
      label: t('common_text_action', { ns: 'common/base' }),
      minWidth: 100,
      align: 'center',
      render: (record: ITruck) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onArchive(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Truck = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateTrucks, canViewTrucks, canEditTrucks, canDeleteTrucks } = usePermissions(TRUCK_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/truck', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isRefreshTruckList } = useAppSelector((state) => state.truck);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_TRUCK);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamTruck>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [truckList, setTruckList] = useState<ITruck[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [idsTruck, setIdsTruck] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<ITruck[]>([]);
  const [idTruck, setIdTruck] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isOpenTruckFormModal, setIsOpenTruckFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListTruck(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshTruckList) return;

    handleGetListTruck(paramObject);

    dispatch(truckActions.setRefreshTruckList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTruckList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListTruck = (params: IQueryParamTruck) => {
    setIsLoading(true);

    dispatch(getListTruck(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setTruckList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdTruck(id);
    setIsOpenTruckFormModal(true);
  };

  const handleArchive = (id: string) => {
    if (!id) return;

    setIdTruck(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdTruck(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteTruck(idTruck))
      .unwrap()
      .then(() => {
        setIdTruck(EMPTY_STRING);
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowsSelect = (ids: string[], selectedItems?: ITruck[]) => {
    setIdsTruck(ids);
    setSelectedItems(selectedItems || []);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowTruckFormModal = () => {
    setIsOpenTruckFormModal((prev) => !prev);
    setIdTruck(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <Box id='truckPage' className={cx('container')}>
      <BaseToolbar title={t('truck_title_management')}>
        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateTrucks && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowTruckFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('truck_text_add_truck')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewTrucks ? truckList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsTruck(t, sortBy, handleSort, handleEdit, handleArchive, canEditTrucks, canDeleteTrucks)}
          onPageChange={handlePageChange}
          // dataSelectedIds={idsTruck}
          // onSelectChange={handleRowsSelect}
          {...(canEditTrucks ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isOpenTruckFormModal && <TruckFormModal onClose={handleShowTruckFormModal} idTruck={idTruck} />}
    </Box>
  );
};

export default Truck;
